import { useRef, useState, useEffect } from "react";
import {
  Star,
  Quote,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { TESTIMONIALS_CONFIG } from "@/config/constants";
import { VturbPlayer } from "@/components/VturbPlayer";

// =============================================
// Tipos e dados
// =============================================

type Testimonial = {
  name: string;
  age: number;
  city: string;
  text: string; // resumo curto
  fullText?: string; // depoimento completo (opcional)
  rating: number;
  videoId: string; // ID do vídeo vturb
  videoUrl: string; // URL do script do vturb
};

const testimonials: Testimonial[] = [
  {
    name: "Maria Clara",
    age: 26,
    city: "Piauí - PI",
    text:
      "Em menos de um mês de mentoria com o Marlon, minha vida mudou completamente. Aprendi o valor do dinheiro e comecei a reconstruir minha história.",
    fullText:
      "Oi, gente, meu nome é Maria Clara. Em menos de um mês de mentoria com o Marlon, minha vida mudou drasticamente. Aprendi o valor do dinheiro e comecei a aplicar, de verdade, as técnicas do método. Eu achava que ia parar sozinha, mas a ludopatia é real. A mentoria me mostrou isso e me deu ferramentas. Valorizem cada hora com o Marlon e a Camila: perguntem, façam o questionário, leiam o livro e reconstruam a vida de vocês. Sou muito grata — estou colhendo frutos e sei que daqui pra frente é só mudança.",
    rating: 5,
    videoId: TESTIMONIALS_CONFIG.mariaClara.id,
    videoUrl: TESTIMONIALS_CONFIG.mariaClara.url,
  },
  {
    name: "Márcia Soares",
    age: 35,
    city: "Espirito Santo - ES",
    text:
      "Eu cheguei no fundo do poço, sem dinheiro nem pra comprar pão pra minha filha. Depois do Método LDA, recuperei minha vida, minha família e minha liberdade.",
    fullText:
      "Antes do método eu era totalmente viciada, não passava um dia sem entrar na plataforma. Perdi todo o meu dinheiro — nem pão pra minha filha eu tinha. Com o Método Libertação das Apostas, entendi meus gatilhos, me conheci de verdade e virei a chave. Hoje guardo dinheiro, invisto, pago as contas em dia e recuperei meu relacionamento e a paz com a minha família. Sou muito grata ao Marlon e à Camila pelo apoio 100% presente. Eu achei que não tinha saída — hoje estou liberta.",
    rating: 5,
    videoId: TESTIMONIALS_CONFIG.marciaSoares.id,
    videoUrl: TESTIMONIALS_CONFIG.marciaSoares.url,
  },
  {
    name: "Jefferson",
    age: 21,
    city: "São João Evangelista - MG",
    text:
      "Passei seis anos preso no vício das apostas, perdi tudo e achei que não tinha mais saída. Depois da mentoria, consegui parar já na primeira aula. Hoje estou limpo e recuperando minha vida.",
    fullText:
      "Fiquei seis anos preso no vício das apostas, apostando o que eu tinha e o que não tinha. Pegava dinheiro emprestado, perdia tudo e recomeçava. Por causa disso, perdi meu casamento e a paz. Um dia, vi uma live do Marlon no TikTok e percebi que eu precisava de ajuda. Entrei na Mentoria Libertação das Apostas sem esperança, mas logo na primeira aula consegui parar de apostar. Hoje, com pouco mais de um mês e meio de acompanhamento, recuperei o controle da vida, voltei a ter tranquilidade e fé no futuro. Se você acha que não tem saída, reconheça o vício e busque ajuda — sozinho é quase impossível, mas com o método certo dá pra se libertar.",
    rating: 5,
    videoId: TESTIMONIALS_CONFIG.jefferson.id,
    videoUrl: TESTIMONIALS_CONFIG.jefferson.url,
  },
  {
    name: "Patrícia",
    age: 35,
    city: "Recife - PE",
    text:
      "Passei três anos viciada em jogos online, perdi dinheiro, amizades e quase minha vida. Depois da mentoria, recuperei minha dignidade, minha família e voltei a viver de verdade.",
    fullText:
      "Sou Patrícia, tenho 35 anos, e vivi três anos presa no vício dos jogos online. O último ano foi o pior: fiquei endividada, mentia pra conseguir dinheiro, perdi amizades, respeito e amor-próprio. Eu tentava parar, fazia promessas, mas sempre recaía. Tudo começou a mudar quando conheci o Método Libertação das Apostas e recebi o apoio do Marlon e da Camila. Eles me ajudaram a mudar minha mente, minha rotina e minha vida. Hoje voltei a participar da vida da minha família, passeio com meus filhos, compro roupas novas, caminho de cabeça erguida. Ainda é uma luta diária, mas a cada dia eu venço. Recuperei minha vida financeira e emocional, e prometi a mim mesma: nunca mais quero aquela vida. Se eu consegui, qualquer um consegue — só é preciso reconhecer o vício e pedir ajuda.",
    rating: 5,
    videoId: TESTIMONIALS_CONFIG.patricia.id,
    videoUrl: TESTIMONIALS_CONFIG.patricia.url,
  },
];

export const Testimonials = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [activeIframe, setActiveIframe] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [hasCounted, setHasCounted] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const toggleExpand = (idx: number) => {
    setExpandedIndex((prev) => (prev === idx ? null : idx));
  };

  const scrollByAmount = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9; // quase uma "página" de cada vez
    el.scrollBy({
      left: dir === "right" ? amount : -amount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("depoimentos");
      if (section && !hasCounted) {
        const { top } = section.getBoundingClientRect();
        if (top <= window.innerHeight && top >= 0) {
          setHasCounted(true);
          let currentCount = 0;
          const targetCount = 1200;
          const increment = Math.ceil(targetCount / 200); // Incremento a cada 10ms

          const interval = setInterval(() => {
            currentCount += increment;
            if (currentCount >= targetCount) {
              currentCount = targetCount;
              clearInterval(interval);
            }
            setCount(currentCount);
          }, 10);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasCounted]);

  return (
    <section className="py-16 md:py-20 relative" id="depoimentos">
      {/* Efeito de gradiente azul claro no topo */}
      <div className="absolute top-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-primary/15 to-transparent pointer-events-none" />

      {/* CSS para esconder a barra de rolagem */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="container px-4 relative z-10">
        {/* Título */}
        <div className="text-center mb-10 md:mb-16 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Histórias reais de{" "}
            <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
              Libertação
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Veja o que dizem pessoas que recuperaram suas vidas
          </p>
        </div>

        {/* Carrossel */}
        <div className="relative max-w-6xl mx-auto">
          {/* Botões laterais (desktop) */}
          <button
            aria-label="Anterior"
            onClick={() => scrollByAmount("left")}
            className="hidden md:flex absolute md:-left-16 left-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full border border-primary/40 bg-background/80 backdrop-blur hover:bg-background/90 shadow-md hover:shadow-lg transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            aria-label="Próximo"
            onClick={() => scrollByAmount("right")}
            className="hidden md:flex absolute md:-right-16 right-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full border border-primary/40 bg-background/80 backdrop-blur hover:bg-background/90 shadow-md hover:shadow-lg transition"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Faixa rolável */}
          <div
            ref={trackRef}
            className="flex items-stretch px-4 sm:px-6 md:px-10 gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-px-4 pb-4 no-scrollbar [scrollbar-width:none] [scrollbar-color:transparent_transparent]"
          >
            {testimonials.map((t, index) => {
              const isExpanded = expandedIndex === index;
              const textId = `testimonial-text-${index}`;

              return (
                <div
                  key={index}
                  className="snap-start shrink-0 w-[88%] sm:w-[70%] md:w-[55%] lg:w-[33.333%]"
                >
                  <div className="group flex flex-col h-full backdrop-blur-xl bg-card/40 rounded-2xl border border-primary/20 hover:border-primary/40 transition-all duration-500 hover:shadow-[0_8px_32px_rgba(100,192,242,0.2)] hover:-translate-y-2 overflow-hidden">
                    {/* Vídeo vturb */}
                    <VturbPlayer
                      videoId={t.videoId}
                      scriptUrl={t.videoUrl}
                      className="relative w-full rounded-t-2xl overflow-hidden"
                    />

                    {/* Conteúdo textual */}
                    <div className="p-5 sm:p-6 flex flex-col flex-1">
                      <Quote className="w-7 h-7 text-accent/30 mb-3" />

                      <div className="flex gap-1 mb-3">
                        {[...Array(t.rating)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 fill-accent text-accent"
                          />
                        ))}
                      </div>

                      {/* Depoimento com colapso/expand */}
                      <div
                        id={textId}
                        className={`relative text-sm sm:text-base text-muted-foreground leading-relaxed transition-all duration-300 ${
                          isExpanded ? "max-h-[1000px]" : "max-h-24 sm:max-h-28 overflow-hidden"
                        }`}
                      >
                        <p className="mb-3">"{t.text}"</p>

                        {t.fullText && (
                          <p
                            className={`whitespace-pre-line ${
                              isExpanded ? "opacity-100" : "opacity-0"
                            } transition-opacity duration-300`}
                          >
                            {t.fullText}
                          </p>
                        )}

                        {!isExpanded && (
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-card to-transparent" />
                        )}
                      </div>

                      {t.fullText && (
                        <button
                          className="mt-3 inline-flex items-center gap-2 text-accent text-sm sm:text-base font-semibold hover:underline"
                          onClick={() => toggleExpand(index)}
                          aria-expanded={isExpanded}
                          aria-controls={textId}
                        >
                          <FileText className="w-4 h-4" />
                          {isExpanded ? (
                            <>
                              Ver menos <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Ver depoimento completo{" "}
                              <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      )}

                      {/* Rodapé autor */}
                      <div className="pt-4 mt-6 border-t border-primary/10">
                        <p className="font-bold text-foreground">{t.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t.age} anos • {t.city}
                        </p>
                      </div>
                    </div>

                    {/* Glow */}
                    <div className="absolute inset-0 bg-gradient-radial from-accent/5 via-transparent to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dica mobile */}
          <div className="mt-3 text-center md:hidden text-xs sm:text-sm text-muted-foreground">
            Arraste para o lado para ver mais depoimentos
          </div>
        </div>

        {/* Rodapé */}
        <div
          className="text-center mt-10 md:mt-12 animate-slide-up"
          style={{ animationDelay: "0.5s" }}
        >
          <p className="text-base sm:text-lg text-muted-foreground">
            <span className="text-accent font-bold">
              +{count.toLocaleString()} pessoas
            </span>{" "}
            já transformaram suas vidas com o Método LDA
          </p>
        </div>
      </div>

      {/* Modal (mantido, caso você queira acionar depois) */}
      {activeIframe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-xl">
            <button
              onClick={() => setActiveIframe(null)}
              className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
              aria-label="Fechar vídeo"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="aspect-video bg-black">
              <iframe
                className="w-full h-full"
                src={activeIframe}
                loading="lazy"
                title="Depoimento em destaque"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
